import { JSBI, Percent, Token } from '@pancakeswap/sdk'
import { BigNumber } from '@ethersproject/bignumber'
import {
  grxChainTokens,
  robinhoodTestnetTokens,
  robinhoodMainnetTokens,
} from './tokens'
import {
  GRX_CHAIN_ID,
  ROBINHOOD_TESTNET_CHAIN_ID,
  ROBINHOOD_MAINNET_CHAIN_ID,
} from './chains'

export const ROUTER_ADDRESS: Record<number, string> = {
  [GRX_CHAIN_ID]: '0x28fC93b8a20570f2B59d5CA9f8a1dA02C4DBcDF5',
  [ROBINHOOD_TESTNET_CHAIN_ID]: '0x49e7fc1466fc9ec82cc6f5e5b368f33f07007559',
  [ROBINHOOD_MAINNET_CHAIN_ID]: '0x872a7222E5ecBD8366b4341d2e9c8bc8b17fbE8e',
}

type ChainTokenList = {
  readonly [chainId: number]: Token[]
}

export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  [GRX_CHAIN_ID]: [
    grxChainTokens.wgrx,
    grxChainTokens.usdt,
  ],
  [ROBINHOOD_TESTNET_CHAIN_ID]: [
    robinhoodTestnetTokens.weth,
    robinhoodTestnetTokens.gtst,
  ],
  [ROBINHOOD_MAINNET_CHAIN_ID]: [
    robinhoodMainnetTokens.weth,
    robinhoodMainnetTokens.gxtest,
  ],
}

export const ADDITIONAL_BASES: {
  readonly [chainId: number]: {
    readonly [tokenAddress: string]: Token[]
  }
} = {
  [GRX_CHAIN_ID]: {},
  [ROBINHOOD_TESTNET_CHAIN_ID]: {},
  [ROBINHOOD_MAINNET_CHAIN_ID]: {},
}

export const CUSTOM_BASES: {
  readonly [chainId: number]: {
    readonly [tokenAddress: string]: Token[]
  }
} = {
  [GRX_CHAIN_ID]: {},
  [ROBINHOOD_TESTNET_CHAIN_ID]: {},
  [ROBINHOOD_MAINNET_CHAIN_ID]: {},
}

export const SUGGESTED_BASES: ChainTokenList = {
  [GRX_CHAIN_ID]: [
    grxChainTokens.wgrx,
    grxChainTokens.usdt,
  ],
  [ROBINHOOD_TESTNET_CHAIN_ID]: [
    robinhoodTestnetTokens.weth,
    robinhoodTestnetTokens.gtst,
  ],
  [ROBINHOOD_MAINNET_CHAIN_ID]: [
    robinhoodMainnetTokens.weth,
    robinhoodMainnetTokens.gxtest,
  ],
}

export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  [GRX_CHAIN_ID]: [
    grxChainTokens.wgrx,
    grxChainTokens.usdt,
  ],
  [ROBINHOOD_TESTNET_CHAIN_ID]: [
    robinhoodTestnetTokens.weth,
    robinhoodTestnetTokens.gtst,
  ],
  [ROBINHOOD_MAINNET_CHAIN_ID]: [
    robinhoodMainnetTokens.weth,
    robinhoodMainnetTokens.gxtest,
  ],
}

export const PINNED_PAIRS: {
  readonly [chainId: number]: [Token, Token][]
} = {
  [GRX_CHAIN_ID]: [
    [grxChainTokens.wgrx, grxChainTokens.usdt],
  ],
  [ROBINHOOD_TESTNET_CHAIN_ID]: [
    [robinhoodTestnetTokens.weth, robinhoodTestnetTokens.gtst],
  ],
  [ROBINHOOD_MAINNET_CHAIN_ID]: [
    [robinhoodMainnetTokens.weth, robinhoodMainnetTokens.gxtest],
  ],
}

export const NetworkContextName = 'NETWORK'

// default allowed slippage, in bips
export const INITIAL_ALLOWED_SLIPPAGE = 50
// 20 minutes, denominated in seconds
export const DEFAULT_DEADLINE_FROM_NOW = 60 * 20

export const BIG_INT_ZERO = JSBI.BigInt(0)

// one basis point
export const ONE_BIPS = new Percent(JSBI.BigInt(1), JSBI.BigInt(10000))
export const BIPS_BASE = JSBI.BigInt(10000)
// used for warning states
export const ALLOWED_PRICE_IMPACT_LOW: Percent = new Percent(JSBI.BigInt(100), BIPS_BASE) // 1%
export const ALLOWED_PRICE_IMPACT_MEDIUM: Percent = new Percent(JSBI.BigInt(300), BIPS_BASE) // 3%
export const ALLOWED_PRICE_IMPACT_HIGH: Percent = new Percent(JSBI.BigInt(500), BIPS_BASE) // 5%
// if the price slippage exceeds this number, force the user to type 'confirm' to execute
export const PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN: Percent = new Percent(JSBI.BigInt(1000), BIPS_BASE) // 10%
// for non expert mode disable swaps above this
export const BLOCKED_PRICE_IMPACT_NON_EXPERT: Percent = new Percent(JSBI.BigInt(1500), BIPS_BASE) // 15%

// used to ensure the user doesn't send so much BNB so they end up with <.01
export const MIN_BNB: JSBI = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(16)) // .01 BNB
export const BETTER_TRADE_LESS_HOPS_THRESHOLD = new Percent(JSBI.BigInt(50), JSBI.BigInt(10000))

export const ZERO_PERCENT = new Percent('0')
export const ONE_HUNDRED_PERCENT = new Percent('1')

// SDN OFAC addresses
export const BLOCKED_ADDRESSES: string[] = [
  '0x7F367cC41522cE07553e823bf3be79A889DEbe1B',
  '0xd882cFc20F52f2599D84b8e8D58C7FB62cfE344b',
  '0x901bb9583b24D97e995513C6778dc6888AB6870e',
  '0xA7e5d5A720f06526557c513402f2e6B5fA20b008',
  '0x8576aCC5C05D6Ce88f4e49bf65BdF0C62F91353C',
]

export { default as farmsConfig } from './farms'
export { default as poolsConfig } from './pools'
export { default as ifosConfig } from './ifo'

export const FAST_INTERVAL = 10000
export const SLOW_INTERVAL = 60000

export const NOT_ON_SALE_SELLER = '0x0000000000000000000000000000000000000000'

// Gelato uses this address to define a native currency in all chains
export const GELATO_NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
// Handler string is passed to Gelato to use PCS router
export const GELATO_HANDLER = 'pancakeswap'
export const GENERIC_GAS_LIMIT_ORDER_EXECUTION = BigNumber.from(500000)

export const EXCHANGE_DOCS_URLS = 'https://docs.pancakeswap.finance/products/pancakeswap-exchange'
export const LIMIT_ORDERS_DOCS_URL = 'https://docs.pancakeswap.finance/products/pancakeswap-exchange/limit-orders'
